import React, { useState } from 'react';
import { IconType } from 'react-icons';
import {
  FiActivity,
  FiCompass,
  FiGrid,
  FiHome,
  FiLink2,
  FiPieChart,
  FiSettings,
  FiTrendingUp,
} from 'react-icons/fi';
import { Link, NavLink } from 'react-router-dom';
import './HeaderMenuModern.css';
import ParametersDialog from '../ParametersDialog';

const menu = [
  { label: 'Accueil', path: '/', icon: FiHome },
  { label: 'Dashboard', path: '/dashboard', icon: FiGrid },
  { label: 'Portefeuille', path: '/unified-portfolio', icon: FiPieChart },
  { label: 'Marché', path: '/market', icon: FiTrendingUp },
  { label: 'Asset Scanner', path: '/asset-scanner', icon: FiActivity },
  { label: 'Pattern Scanner', path: '/pattern-scanner', icon: FiCompass },
  { label: 'Connexions', path: '/connections', icon: FiLink2 },
];

interface HeaderMenuModernProps {
  user: { name?: string; login?: string; role?: string } | null;
  onSettings?: () => void;
  onThemeChange?: (theme: string) => void;
}

function resolveUserDisplayName(user: { name?: string; login?: string; role?: string } | null) {
  if (typeof user?.name === 'string' && user.name.trim()) {
    return user.name.trim();
  }
  if (typeof user?.login === 'string' && user.login.trim()) {
    return user.login.trim();
  }
  return '';
}

function getUserInitials(user: { name?: string; login?: string; role?: string } | null) {
  const displayName = resolveUserDisplayName(user);
  if (!displayName) {
    return 'EA';
  }

  const parts = displayName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  if (parts.length === 0) {
    return 'EA';
  }

  return parts.map((part) => part[0]?.toUpperCase() ?? '').join('');
}

function MenuLink({ label, path, icon: Icon }: { label: string; path: string; icon: IconType }) {
  return (
    <NavLink
      to={path}
      end={path === '/'}
      className={({ isActive }) => `nav-link-modern${isActive ? ' nav-link-modern--active' : ''}`}
    >
      <span className="nav-icon-modern" aria-hidden="true">
        <Icon />
      </span>
      <span className="nav-label-modern">{label}</span>
    </NavLink>
  );
}

const HeaderMenuModern: React.FC<HeaderMenuModernProps> = ({ user, onSettings, onThemeChange }) => {
  const [paramsOpen, setParamsOpen] = useState(false);
  const userInitials = getUserInitials(user);
  const userDisplayName = resolveUserDisplayName(user) || 'Utilisateur';

  const handleSettings = () => {
    setParamsOpen(true);
    if (onSettings) onSettings();
  };

  return (
    <header className="header-modern">
      <div className="header-inner">
        <Link className="logo" to="/">
          <span className="logo-mark" aria-hidden="true">
            <svg className="logo-icon" width="32" height="32" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
              <rect className="bar bar-1" x="8" y="14" width="40" height="6" rx="3" />
              <rect className="bar bar-2" x="12" y="30" width="40" height="6" rx="3" />
              <rect className="bar bar-3" x="16" y="46" width="40" height="6" rx="3" />
            </svg>
          </span>
          <span className="logo-copy">
            <span className="logo-text logo-text-full">
              Ecotone<span className="logo-ai">AI</span>
            </span>
            <span className="logo-text logo-text-short">
              Eco<span className="logo-ai">AI</span>
            </span>
            <span className="logo-tagline">Macro. Patterns. Exécution.</span>
          </span>
        </Link>

        <nav className="nav-modern">
          <ul className="nav-list-modern">
            {menu.map((item) => (
              <li key={item.label} className="nav-item-modern">
                <MenuLink label={item.label} path={item.path} icon={item.icon} />
              </li>
            ))}
          </ul>
        </nav>

        <div className="nav-right-modern">
          {user ? (
            <div className="nav-user-modern" title={userDisplayName}>
              <span className="nav-user-modern__avatar">{userInitials}</span>
              <span className="nav-user-modern__meta">
                <span className="nav-user-modern__name">{userDisplayName}</span>
                <span className="nav-user-modern__role">{user.role || 'Utilisateur'}</span>
              </span>
            </div>
          ) : (
            <Link to="/login" className="nav-admin-modern nav-admin-modern--ghost">Se connecter</Link>
          )}

          {user && user.role && user.role.toUpperCase() === 'ADMIN' && (
            <Link to="/admin" className="nav-admin-modern">Admin</Link>
          )}

          <button className="nav-settings-modern" onClick={handleSettings} title="Paramètres" type="button">
            <FiSettings />
          </button>
        </div>
      </div>

      <ParametersDialog
        open={paramsOpen}
        onClose={() => setParamsOpen(false)}
        onThemeChange={onThemeChange || (() => {})}
        onCurrencyChange={() => {}}
      />
    </header>
  );
};

export default HeaderMenuModern;
